import React from 'react';
import { Bell, Moon, Clock, Smartphone, AlertTriangle } from 'lucide-react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const NotificationControlsSection = ({ formData, handleInputChange, handleCheckboxChange, errors, activePreset, onPresetChange }) => {
  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <Bell className="w-6 h-6 text-accent-blue" />
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-foreground">Notification Controls</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure notification types, quiet hours, and alert preferences
          </p>
        </div>
        {/* Quick Preset Selector */}
        {onPresetChange && (
          <div className="w-64">
            <Select
              value={activePreset || 'custom'}
              onChange={(e) => onPresetChange?.(e?.target?.value)}
              options={[
                { value: 'custom', label: '⚙️ Custom Configuration' },
                { value: 'home-trips', label: '🏠 Home Trips' },
                { value: 'international', label: '✈️ International Routes' },
                { value: 'tight-connections', label: '⚡ Tight Connections' }
              ]}
              className="w-full"
            />
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Notification Types */}
        <div>
          <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notification Types
          </h3>
          <div className="space-y-3 pl-6">
            <Checkbox
              id="notifyFlightDelays"
              label="Flight Delays"
              description="Get notified when flights are delayed"
              checked={formData?.notifyFlightDelays ?? true}
              onChange={(checked) => handleCheckboxChange('notifyFlightDelays', checked)}
            />
            <Checkbox
              id="notifyGateChanges"
              label="Gate Changes"
              description="Receive alerts for gate changes"
              checked={formData?.notifyGateChanges ?? true}
              onChange={(checked) => handleCheckboxChange('notifyGateChanges', checked)}
            />
            <Checkbox
              id="notifyScheduleChanges"
              label="Schedule Changes"
              description="Alert when your schedule is modified"
              checked={formData?.notifyScheduleChanges ?? true}
              onChange={(checked) => handleCheckboxChange('notifyScheduleChanges', checked)}
            />
            <Checkbox
              id="notifyCompliance"
              label="Compliance Alerts"
              description="FAR 117 and rest requirement violations"
              checked={formData?.notifyCompliance ?? true}
              onChange={(checked) => handleCheckboxChange('notifyCompliance', checked)}
            />
            <Checkbox
              id="notifyPayDiscrepancies"
              label="Pay Discrepancies"
              description="Notify when pay calculations differ from expected"
              checked={formData?.notifyPayDiscrepancies ?? true}
              onChange={(checked) => handleCheckboxChange('notifyPayDiscrepancies', checked)}
            />
            <Checkbox
              id="notifyWeatherAlerts"
              label="Weather Alerts"
              description="Severe weather affecting your flights"
              checked={formData?.notifyWeatherAlerts ?? false}
              onChange={(checked) => handleCheckboxChange('notifyWeatherAlerts', checked)}
            />
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="pt-4 border-t border-border">
          <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Moon className="w-4 h-4" />
            Quiet Hours
          </h3>
          <div className="space-y-4 pl-6">
            <Checkbox
              id="enableQuietHours"
              label="Enable Quiet Hours"
              description="Silence non-critical notifications during specified times"
              checked={formData?.enableQuietHours ?? false}
              onChange={(checked) => handleCheckboxChange('enableQuietHours', checked)}
            />
            
            {formData?.enableQuietHours && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Start Time
                  </label>
                  <Input
                    type="time"
                    name="quietHoursStart"
                    value={formData?.quietHoursStart || "22:00"}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    End Time
                  </label>
                  <Input
                    type="time"
                    name="quietHoursEnd"
                    value={formData?.quietHoursEnd || "07:00"}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
              </div>
            )}
            
            {formData?.enableQuietHours && (
              <Checkbox
                id="quietHoursCriticalOverride"
                label="Allow Critical Alerts During Quiet Hours"
                description="Critical delays and compliance violations will still notify"
                checked={formData?.quietHoursCriticalOverride ?? true}
                onChange={(checked) => handleCheckboxChange('quietHoursCriticalOverride', checked)}
              />
            )}
          </div>
        </div>

        {/* Alert Frequency */}
        <div className="pt-4 border-t border-border">
          <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Alert Frequency
          </h3>
          <div className="space-y-4 pl-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Notification Frequency
              </label>
              <Select
                name="notificationFrequency"
                value={formData?.notificationFrequency || "realtime"}
                onChange={handleInputChange}
                options={[
                  { value: "realtime", label: "Real-time (Immediate)" },
                  { value: "batched-15", label: "Batched (Every 15 minutes)" },
                  { value: "batched-30", label: "Batched (Every 30 minutes)" },
                  { value: "batched-60", label: "Batched (Every hour)" },
                  { value: "digest", label: "Daily Digest" }
                ]}
                className="w-full md:w-96"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Critical alerts are always sent in real-time regardless of this setting
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Delay Alert Threshold
              </label>
              <Select
                name="delayAlertThreshold"
                value={formData?.delayAlertThreshold || "15"}
                onChange={handleInputChange}
                options={[
                  { value: "0", label: "Any delay" },
                  { value: "15", label: "15+ minutes" },
                  { value: "30", label: "30+ minutes" },
                  { value: "60", label: "1+ hour" },
                  { value: "120", label: "2+ hours" }
                ]}
                className="w-full md:w-96"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Only notify for delays exceeding this duration
              </p>
            </div>

            <Checkbox
              id="groupSimilarAlerts"
              label="Group Similar Alerts"
              description="Combine multiple alerts of the same type into one notification"
              checked={formData?.groupSimilarAlerts ?? true}
              onChange={(checked) => handleCheckboxChange('groupSimilarAlerts', checked)}
            />
          </div>
        </div>

        {/* SMS Alerts for Critical Delays */}
        <div className="pt-4 border-t border-border">
          <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Smartphone className="w-4 h-4" />
            SMS Alerts for Critical Delays
          </h3>
          <div className="space-y-4 pl-6">
            <Checkbox
              id="enableSmsAlerts"
              label="Enable SMS Alerts"
              description="Receive text messages for critical flight delays and emergencies"
              checked={formData?.enableSmsAlerts ?? false}
              onChange={(checked) => handleCheckboxChange('enableSmsAlerts', checked)}
            />

            {formData?.enableSmsAlerts && (
              <>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    name="smsPhoneNumber"
                    value={formData?.smsPhoneNumber || ""}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    className="w-full md:w-96"
                    error={errors?.smsPhoneNumber}
                  />
                  {errors?.smsPhoneNumber && (
                    <p className="text-xs text-red-500 mt-1">{errors?.smsPhoneNumber}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Critical Delay Threshold for SMS
                  </label>
                  <Select
                    name="smsCriticalThreshold"
                    value={formData?.smsCriticalThreshold || "60"}
                    onChange={handleInputChange}
                    options={[
                      { value: "30", label: "30+ minutes" },
                      { value: "60", label: "1+ hour" },
                      { value: "120", label: "2+ hours" },
                      { value: "180", label: "3+ hours" }
                    ]}
                    className="w-full md:w-96"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Only send SMS for delays exceeding this critical threshold
                  </p>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-1">SMS Alerts Include:</h4>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• Flight delays exceeding critical threshold</li>
                        <li>• Flight cancellations affecting your schedule</li>
                        <li>• FAR 117 compliance violations</li>
                        <li>• Emergency schedule changes requiring immediate action</li>
                      </ul>
                      <p className="text-xs text-muted-foreground mt-2">
                        Standard SMS rates may apply. Critical alerts only.
                      </p>
                    </div>
                  </div>
                </div>

                <Checkbox
                  id="smsBackupOnly"
                  label="Use SMS as Backup Only"
                  description="Only send SMS if push notifications fail to deliver"
                  checked={formData?.smsBackupOnly ?? false}
                  onChange={(checked) => handleCheckboxChange('smsBackupOnly', checked)}
                />
              </>
            )}
          </div>
        </div>

        {/* Notification Channels */}
        <div className="pt-4 border-t border-border">
          <h3 className="text-base font-semibold text-foreground mb-4">Notification Channels</h3>
          <div className="space-y-3 pl-6">
            <Checkbox
              id="enablePushNotifications"
              label="Push Notifications"
              description="In-app and browser push notifications"
              checked={formData?.enablePushNotifications ?? true}
              onChange={(checked) => handleCheckboxChange('enablePushNotifications', checked)}
            />
            <Checkbox
              id="emailNotifications"
              label="Email Notifications"
              description="Receive notifications via email"
              checked={formData?.emailNotifications ?? true}
              onChange={(checked) => handleCheckboxChange('emailNotifications', checked)}
            />
            {formData?.emailNotifications && (
              <div className="ml-6 mt-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Notification Email
                </label>
                <Input
                  type="email"
                  name="notificationEmail"
                  value={formData?.notificationEmail || ""}
                  onChange={handleInputChange}
                  placeholder="pilot@example.com"
                  className="w-full md:w-96"
                  error={errors?.notificationEmail}
                />
                {errors?.notificationEmail && (
                  <p className="text-xs text-red-500 mt-1">{errors?.notificationEmail}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationControlsSection;